import { useEffect } from 'react';
import { subscribeTopic, unsubscribeTopic } from '@/utils/notificationsSocket';

/**
 * Subscribes to the realtime `pdf:{id}` gateway topic for each in-flight PDF and
 * invokes `onStatusChange` whenever the backend pushes a status update (or the
 * initial snapshot). Replaces the previous 5s polling of `/api/v1/task/`.
 *
 * The `pdf.status_update` payload carries only `{pdfId, status, statusDisplay}`
 * (no file URL), so the caller reacts by refetching the task query, which
 * returns the download URL for completed PDFs.
 *
 * @param pdfIds  Pdf UUIDs (the `notificationId` field from the notifications list).
 */
export const usePdfStatusSocket = (pdfIds: string[], onStatusChange: () => void): void => {
  // Stable primitive dep so we only re-subscribe when the actual set changes.
  const key = pdfIds.slice().sort().join(',');

  useEffect(() => {
    if (!key) return;
    const topics = key.split(',').map((id) => `pdf:${id}`);
    topics.forEach((topic) => subscribeTopic(topic, () => onStatusChange()));

    return () => {
      topics.forEach((topic) => unsubscribeTopic(topic));
    };
    // onStatusChange is expected to be stable (useCallback in the caller).
  }, [key, onStatusChange]);
};
